/**
 * Export an analysis to a JSON file the analyst can open, diff, archive, or
 * hand to another tool for further reasoning.
 *
 * Deliberately client-side: the page already holds the complete record, so
 * the file matches exactly what the analyst is looking at. A server endpoint
 * could silently export a different (newer, or differently filtered) version
 * than the one on screen.
 */

/** What produced the file — kept in the payload so it stays self-describing
 *  once it has been renamed, moved, or pasted into another tool. */
export interface ExportMeta {
  kind: 'issue-analysis' | 'cr-analysis';
  /** Ticket or CR key. */
  key: string;
}

/** Build the wrapped payload. Separated from the download so it can be
 *  tested and reused (clipboard, preview) without touching the DOM. */
export function buildExport(meta: ExportMeta, analysis: unknown) {
  return {
    export: {
      ...meta,
      exported_at: new Date().toISOString(),
      source: 'LISA — Liner Support Assistant',
      schema_note:
        'Exported verbatim from the analysis record. Field names match the LISA API; ' +
        'absent fields mean the step did not run, not that it returned nothing.',
      contains_customer_data: true,
    },
    analysis,
  };
}

/** `FMSC-3445-issue-analysis-2026-08-05.json` — sorts sensibly and survives
 *  being downloaded twice (the browser appends its own counter). */
export function exportFilename(meta: ExportMeta, now = new Date()): string {
  const date = now.toISOString().slice(0, 10);
  const safeKey = meta.key.replace(/[^A-Za-z0-9._-]/g, '_') || 'analysis';
  return `${safeKey}-${meta.kind}-${date}.json`;
}

/** Trigger a browser download of the analysis as formatted JSON. */
export function downloadAnalysisJson(meta: ExportMeta, analysis: unknown): void {
  const blob = new Blob([JSON.stringify(buildExport(meta, analysis), null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = exportFilename(meta);
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Without this the blob is held for the lifetime of the document — a real
  // leak on a page where an analyst exports repeatedly.
  URL.revokeObjectURL(url);
}
